/**
 * Phase 8 · Cluster C-44 — GitHub sourcing connector.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.8
 *
 * Queries GitHub's public people search + profile endpoints:
 *   - GET https://api.github.com/search/users?q=<q>
 *   - GET https://api.github.com/users/:login   (per-hit hydrate)
 *
 * Auth:
 *   - auth_kind='api_key'  → personal access token, OPTIONAL. Without a
 *     token we get 30 search requests / minute / IP (GitHub's documented
 *     unauthenticated search ceiling). With a token the authed limit
 *     rises to 5000 / hour / user, but that code path is gated behind
 *     the Edge Function proxy (future work) because we do NOT expose
 *     `encrypted_secret` to the browser. The connector calls the proxy
 *     first; if the proxy is not deployed yet it falls back to direct,
 *     unauthenticated fetch so the feature is usable on day one.
 *
 * Read-only. No repo-list hydration at search time — that's deferred to
 * the "View details" card action per C-44's DON'T list.
 */
import { supabase } from '@core/supabase/client';
import { sourceConnectorsService } from '@core/supabase/services/sourceConnectorsService';
import {
  clampLimit,
  ConnectorNotConfiguredError,
  ConnectorTransportError,
  type ConnectorHealth,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './types';

const UNAUTH_PER_MIN = 30;
const UNAUTH_WINDOW_MS = 60 * 1000;
const AUTHED_PER_HOUR = 5000;
const AUTHED_WINDOW_MS = 60 * 60 * 1000;
const PROXY_FN = 'ggj_b2b_sourcing_proxy';
const GH_SEARCH_URL = 'https://api.github.com/search/users';
const GH_USER_URL = 'https://api.github.com/users';

interface BucketState {
  timestamps: number[];
}
const rateBuckets = new Map<string, BucketState>();

/**
 * Sliding-window limiter. `key` is `${orgId}:${authed ? 'token' : 'ip'}`.
 * The caps differ by path because the GitHub limits themselves differ —
 * we keep them in lockstep so a token-configured org never falls back
 * to the stricter unauth bucket unnecessarily.
 */
function acquireToken(orgId: string, authed: boolean): boolean {
  const cap = authed ? AUTHED_PER_HOUR : UNAUTH_PER_MIN;
  const windowMs = authed ? AUTHED_WINDOW_MS : UNAUTH_WINDOW_MS;
  const key = `github:${orgId}:${authed ? 'a' : 'u'}`;
  const now = Date.now();
  const state = rateBuckets.get(key) ?? { timestamps: [] };
  state.timestamps = state.timestamps.filter((t) => now - t < windowMs);
  if (state.timestamps.length >= cap) {
    rateBuckets.set(key, state);
    return false;
  }
  state.timestamps.push(now);
  rateBuckets.set(key, state);
  return true;
}

interface GhSearchHit {
  login: string;
  id: number;
  avatar_url: string;
  html_url: string;
  score?: number;
}

interface GhSearchResponse {
  total_count?: number;
  incomplete_results?: boolean;
  items?: GhSearchHit[];
}

interface GhUserResponse {
  login: string;
  id: number;
  name?: string | null;
  avatar_url?: string | null;
  html_url?: string | null;
  bio?: string | null;
  blog?: string | null;
  company?: string | null;
  location?: string | null;
  public_repos?: number | null;
  followers?: number | null;
  updated_at?: string | null;
}

/**
 * Turn a SourcingQuery into a GitHub-search `q=` expression. GitHub's
 * search grammar is explicit about qualifiers (`language:`, `location:`);
 * keywords stay unqualified so they match bio / name / login freely.
 */
function buildGithubQuery(q: SourcingQuery): string {
  const parts: string[] = [];
  const keywords = [...(q.keywords ?? [])].map((s) => s.trim()).filter(Boolean);
  if (keywords.length) parts.push(keywords.join(' '));

  // Every skill becomes a `language:` qualifier. GitHub's search union
  // semantics for duplicate qualifiers behave as OR, which is what we
  // want here ("Rust or Go contributor").
  const skills = (q.skills ?? []).map((s) => s.trim()).filter(Boolean);
  for (const lang of skills) {
    parts.push(`language:${lang.replace(/\s+/g, '-')}`);
  }

  if (q.location) {
    const loc = q.location.trim().replace(/\s+/g, '-');
    if (loc) parts.push(`location:${loc}`);
  }

  // `type:user` excludes organizations — we only want people for a
  // candidate-surfacing tool.
  parts.push('type:user');
  return parts.join(' ');
}

function hitToResult(
  hit: GhSearchHit,
  profile: GhUserResponse | null,
  topLanguage: string | undefined,
): SourcingResult {
  const displayName = profile?.name?.trim() || hit.login;
  const headlineParts: string[] = [];
  if (profile?.bio?.trim()) headlineParts.push(profile.bio.trim());
  else if (profile?.company?.trim()) headlineParts.push(profile.company.trim());
  return {
    externalId: String(hit.id ?? hit.login),
    name: displayName,
    headline: headlineParts.join(' · ') || undefined,
    locationText: profile?.location ?? undefined,
    profileUrl: profile?.html_url ?? hit.html_url,
    avatarUrl: profile?.avatar_url ?? hit.avatar_url,
    signals: {
      login: hit.login,
      repos: profile?.public_repos ?? null,
      followers: profile?.followers ?? null,
      topLanguage: topLanguage ?? null,
      blog: profile?.blog || null,
      lastActiveAt: profile?.updated_at ?? null,
    },
    source: 'github',
  };
}

/**
 * Try the Edge Function proxy first so an authed search path is used
 * when available. Returns the raw `{ items, userProfiles }` bundle on
 * success or `null` when the proxy is not deployed — callers fall back
 * to the direct browser path in that case.
 */
async function tryProxy(
  orgId: string,
  q: string,
  limit: number,
): Promise<{ items: GhSearchHit[]; profiles: Record<string, GhUserResponse> } | null> {
  try {
    const { data, error } = await supabase.functions.invoke(PROXY_FN, {
      body: { action: 'github.search', orgId, query: { q, limit } },
    });
    if (error) return null;
    const payload = (data ?? null) as
      | { items?: GhSearchHit[]; profiles?: Record<string, GhUserResponse> }
      | null;
    if (!payload || !Array.isArray(payload.items)) return null;
    return { items: payload.items, profiles: payload.profiles ?? {} };
  } catch {
    return null;
  }
}

/**
 * Direct-from-browser search. GitHub's public API returns
 * `Access-Control-Allow-Origin: *`, so this works without a proxy; the
 * trade-off is we cannot attach the token (no way to read
 * encrypted_secret from the browser) and therefore hit the 30/min limit.
 */
async function directSearch(q: string, limit: number): Promise<GhSearchHit[]> {
  const url = `${GH_SEARCH_URL}?q=${encodeURIComponent(q)}&per_page=${limit}`;
  const res = await fetch(url, { headers: { Accept: 'application/vnd.github+json' } });
  if (!res.ok) {
    throw new ConnectorTransportError('github', `search ${res.status}`);
  }
  const json = (await res.json()) as GhSearchResponse;
  return Array.isArray(json.items) ? json.items.slice(0, limit) : [];
}

async function directProfile(login: string): Promise<GhUserResponse | null> {
  try {
    const res = await fetch(`${GH_USER_URL}/${encodeURIComponent(login)}`, {
      headers: { Accept: 'application/vnd.github+json' },
    });
    if (!res.ok) return null;
    return (await res.json()) as GhUserResponse;
  } catch {
    return null;
  }
}

export const githubConnector: SourcingConnector = {
  kind: 'github',

  async search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]> {
    if (!orgId) return [];

    // A row is optional — GitHub works unauthenticated. But if the admin
    // explicitly saved a row with status='revoked' we honour that and
    // refuse to search; that gives a clean way to block the channel.
    const row = await sourceConnectorsService.get(orgId, 'github');
    if (row && row.status !== 'active') {
      throw new ConnectorNotConfiguredError('github', `status=${row.status}`);
    }
    const hasToken = Boolean(row && row.authKind === 'api_key');

    if (!acquireToken(orgId, hasToken)) {
      throw new ConnectorTransportError(
        'github',
        hasToken
          ? `rate limit exceeded (${AUTHED_PER_HOUR}/hour)`
          : `rate limit exceeded (${UNAUTH_PER_MIN}/minute) — configure a token for higher limits`,
      );
    }

    const limit = clampLimit(query.limit);
    const q = buildGithubQuery(query);

    // Proxy first (uses the token) → fall back to direct.
    const proxyBundle = await tryProxy(orgId, q, limit);
    let hits: GhSearchHit[] = [];
    let profilesFromProxy: Record<string, GhUserResponse> = {};
    if (proxyBundle) {
      hits = proxyBundle.items;
      profilesFromProxy = proxyBundle.profiles;
    } else {
      hits = await directSearch(q, limit);
    }

    // Per-hit hydrate. Batched via Promise.all so we don't wait N*RTT
    // sequentially. GitHub's /users/:login is cheap (1 search "slot" per
    // call counts toward the 60/hour CORE limit, not the 30/min search
    // limit). We cap the fan-out at `limit` which is already ≤ 100.
    const logins = hits.map((h) => h.login).filter(Boolean);
    const profiles: Record<string, GhUserResponse | null> = { ...profilesFromProxy };
    await Promise.all(
      logins.map(async (login) => {
        if (profiles[login]) return;
        profiles[login] = await directProfile(login);
      }),
    );

    // Pick a best-guess `topLanguage` from the requested skills when the
    // caller supplied any — we don't run an extra /repos/:login call.
    const topLanguage = (query.skills ?? [])[0];

    if (row) void sourceConnectorsService.markUsed(orgId, 'github');

    return hits.map((h) => hitToResult(h, profiles[h.login] ?? null, topLanguage));
  },

  async healthcheck(orgId: string): Promise<ConnectorHealth> {
    const row = await sourceConnectorsService.get(orgId, 'github');
    if (!row) return { ok: true, reason: 'unauthenticated' };
    if (row.status !== 'active') return { ok: false, reason: row.status };
    return { ok: true };
  },
};
